// La page « Prise en main » (lot E, docs/chantier-partage-radar.md).
//
// POURQUOI UNE PAGE ET PAS UNE VISITE GUIDÉE. Une modale d'accueil se clique
// pour la faire disparaître, et l'information qu'elle portait n'est plus jamais
// atteignable. Ici c'est une entrée de navigation permanente : c'est le premier
// endroit où l'on revient quand on a oublié comment brancher l'extension, et le
// seul endroit où l'on dit à quelqu'un ce que le produit ne sait PAS faire tout
// seul.
//
// CE QU'ELLE DOIT DIRE, ET QUI EST LE VRAI LIVRABLE :
//  - Radar surveille des pages d'offres, tous les jours, à votre place ;
//  - l'extension marche sur n'importe quel site, après activation ;
//  - un site inconnu demande une intervention humaine, voici comment ça se
//    passe et combien de temps ça prend. Le dire D'AVANCE est la seule façon
//    qu'un premier essai qui échoue ne se lise pas comme une panne ;
//  - ce qui est partagé entre comptes et ce qui ne l'est pas ;
//  - et un endroit pour écrire quand quelque chose ne va pas.
//
// Les étapes portent un état DÉDUIT de la réalité (profil présent, extension
// branchée, sources surveillées) et non une case cochée en mémoire : une
// checklist qui se souvient d'avoir été cochée mentirait dès que l'utilisateur
// change de navigateur ou révoque son jeton.

import { useEffect, useState } from 'react'
import {
  BookOpen,
  CheckCircle2,
  Circle,
  Link2,
  MessageSquare,
  Send,
} from 'lucide-react'
import { get, post, type Summary } from '../lib/api'
import { useData, Loading, ActionButton, ImportCv, useToast } from '../components/common'
import { useT } from '../lib/i18n'
import { useMots } from '../lib/vocabulaire'
import { navigate } from '../lib/router'
import { useMonProfil } from '../lib/formule'
import { InstallerExtension } from '../components/installerExtension'

interface EtatJeton {
  actif: boolean
  createdAt?: string
  lastUsedAt?: string | null
}

function Etape({
  fait,
  titre,
  children,
}: {
  fait: boolean
  titre: string
  children: React.ReactNode
}) {
  return (
    <section className="card etape">
      <h3 className={fait ? 'etapeh faite' : 'etapeh'}>
        {fait ? <CheckCircle2 size={18} /> : <Circle size={18} />}
        {titre}
      </h3>
      <div className="etapec">{children}</div>
    </section>
  )
}

export function PriseEnMain({
  summary,
  onProfilCree,
}: {
  summary: Summary | null
  /** Relit la session : le premier CV crée le profil ET le rattache au compte,
   *  donc la navigation change au même moment. */
  onProfilCree: () => void
}) {
  const t = useT()
  const mots = useMots()
  const profileId = useMonProfil()
  const jeton = useData(() => get<EtatJeton>('/api/extension/token'), [])
  // La connexion de l'extension se fait AILLEURS (dans ses Options) : sans
  // relecture régulière, l'étape resterait « à faire » jusqu'au rechargement.
  const relireJeton = jeton.reload
  useEffect(() => {
    const minuterie = setInterval(relireJeton, 10000)
    return () => clearInterval(minuterie)
  }, [relireJeton])
  const [importOuvert, setImportOuvert] = useState(false)

  const aUnProfil = (profileId ?? -1) > 0
  const aDesSources = (summary?.sourcesTotal ?? 0) > 0
  const brancheee = jeton.data?.actif === true

  if (jeton.loading) return <Loading label={t('common.loading')} />

  return (
    <>
      <div className="bar">
        <div>
          <h3>
            <BookOpen size={17} /> {mots('nav.aide')}
          </h3>
          <div className="sub">{t('aide.intro')}</div>
        </div>
      </div>

      <div className="body aide">
        <section className="card">
          <h3>{t('aide.what.title')}</h3>
          <p className="etapec">{t('aide.what.body')}</p>
          <p className="etapec">{t('aide.what.extension')}</p>
        </section>

        <Etape fait={aUnProfil} titre={t('aide.step.cv.title')}>
          <p>{t('aide.step.cv.body')}</p>
          {/* Un compte tout neuf n'a AUCUNE entrée de navigation vers son
              profil : celle-ci n'apparaît qu'une fois le profil créé (cf. la
              nav dans App.tsx). Cette page porte donc l'import elle-même, sinon
              le premier geste demandé à un invité serait le seul qu'il ne
              pourrait pas faire. */}
          {aUnProfil ? (
            <button className="btn-secondary" onClick={() => navigate('#/profil')}>
              {t('aide.step.cv.cta')}
            </button>
          ) : (
            <button className="btn-primary" onClick={() => setImportOuvert(true)}>
              {t('aide.step.cv.cta')}
            </button>
          )}
        </Etape>

        {importOuvert && (
          <ImportCv
            onClose={() => setImportOuvert(false)}
            // Le profil vient d'être créé ET rattaché au compte côté serveur :
            // on relit la session, sinon la navigation continuerait de croire
            // que ce compte n'a pas de profil jusqu'au prochain rechargement.
            onDone={() => onProfilCree()}
          />
        )}

        {/* « Fait » = une offre est réellement arrivée par l'extension. Rien
            d'autre ne le prouve : ni un clic sur la fiche du Chrome Web Store
            (rien ne dit qu'on l'a ajoutée), ni le jeton (il se fabrique sans
            rien installer). Une coche posée sur une supposition dirait à
            l'utilisateur qu'il a fini alors qu'il n'a rien fait. */}
        <Etape fait={Boolean(jeton.data?.lastUsedAt)} titre={t('aide.step.install.title')}>
          <p>{t('aide.step.install.body')}</p>
          <InstallerExtension />
        </Etape>

        {/* Plus de jeton à fabriquer ni à copier : le bouton « Connecter avec
            Radar » de l'extension obtient un code de cette page tout seul
            (PontExtension), le code s'affiche ici pour qui le tape, et
            l'adresse et le mot de passe restent possibles. L'étape ne fait
            qu'expliquer, et dire si une connexion existe. */}
        <Etape fait={brancheee} titre={t('aide.step.link.title')}>
          <p>{t('aide.step.link.body')}</p>
          <ol className="etapes-liste">
            <li>{t('aide.step.link.s1')}</li>
            <li>{t('aide.step.link.s2')}</li>
            <li>{t('aide.step.link.s3')}</li>
          </ol>
          <p className="field-help">{t('aide.step.link.autrement')}</p>
          <CodeExtension />
          {brancheee && (
            <p className="field-help">
              {jeton.data?.lastUsedAt
                ? t('aide.step.link.lastUsed', { date: jeton.data.lastUsedAt })
                : t('aide.step.link.neverUsed')}
            </p>
          )}
        </Etape>

        <Etape fait={aDesSources} titre={t('aide.step.sites.title')}>
          <p>{t('aide.step.sites.body')}</p>
          {/* LA phrase du lot E. Elle est ici, en clair, et elle est répétée sur
              l'écran Sources au moment exact du refus. */}
          <p className="avertissement">{t('aide.step.sites.warning')}</p>
          <button className="btn-secondary" onClick={() => navigate('#/sources')}>
            <Link2 size={15} /> {t('aide.step.sites.cta')}
          </button>
        </Etape>

        <section className="card">
          <h3>{t('aide.shared.title')}</h3>
          <p className="etapec">{t('aide.shared.body')}</p>
        </section>

        <section className="card">
          <h3>
            <MessageSquare size={16} /> {t('aide.feedback.title')}
          </h3>
          <p className="etapec">{t('aide.feedback.body')}</p>
          <RetourDeveloppeur />
        </section>
      </div>
    </>
  )
}

/** Le canal de retour : le seul contact dans l'application. Partagé par la
 *  prise en main de la console et l'aide de Radar. */
export function RetourDeveloppeur() {
  const t = useT()
  const toast = useToast()
  const [typeRetour, setTypeRetour] = useState('anomalie')
  const [message, setMessage] = useState('')
  const envoyerRetour = async () => {
    const r = await post<{ transmis: boolean }>('/api/feedback', { type: typeRetour, message })
    setMessage('')
    toast(r.transmis ? t('aide.feedback.sent') : t('aide.feedback.stored'), r.transmis ? undefined : 'err')
  }
  return (
    <div className="retour">
      <label className="field-label" htmlFor="retour-type">
        {t('aide.feedback.typeLabel')}
      </label>
      <select
        id="retour-type"
        className="input-field"
        value={typeRetour}
        onChange={(e) => setTypeRetour(e.target.value)}
      >
        <option value="anomalie">{t('aide.feedback.type.bug')}</option>
        <option value="idee">{t('aide.feedback.type.idea')}</option>
        <option value="site">{t('aide.feedback.type.site')}</option>
        <option value="autre">{t('aide.feedback.type.other')}</option>
      </select>
      <label className="field-label" htmlFor="retour-message">
        {t('aide.feedback.messageLabel')}
      </label>
      <textarea
        id="retour-message"
        className="input-field"
        rows={5}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder={t('aide.feedback.placeholder')}
      />
      <ActionButton
        variant="primary"
        busyLabel={t('aide.feedback.sending')}
        onClick={envoyerRetour}
        disabled={message.trim().length < 10}
      >
        <Send size={15} /> {t('aide.feedback.send')}
      </ActionButton>
    </div>
  )
}

/**
 * Le code qui connecte l'extension sans mot de passe (lot 3, cf.
 * web/codesExtension.ts) : dix minutes, une seule fois.
 */
export function CodeExtension() {
  const t = useT()
  const [code, setCode] = useState<{ code: string; expire: number } | null>(null)
  return (
    <div className="code-ext">
      {code ? (
        <>
          <span className="code-ext-v" aria-label={t('aide.code.aria')}>{code.code}</span>
          <span className="field-help">{t('aide.code.aide')}</span>
        </>
      ) : (
        <ActionButton size="sm" onClick={async () => setCode(await post<{ code: string; expire: number }>('/api/auth/extension/code', {}))}>
          {t('aide.code.bouton')}
        </ActionButton>
      )}
    </div>
  )
}

